import React, { useCallback } from 'react';

import { type ExcalidrawElement } from '@excalidraw/excalidraw/types/element/types';
import { type BinaryFiles, type ExcalidrawImperativeAPI } from '@excalidraw/excalidraw/types/types';
import { KeyboardArrowUp } from '@mui/icons-material';
import {
    Accordion, AccordionDetails, AccordionSummary,
    Box,
    Grid,
    Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';

import { type LibraryGroup } from '../../../types';
import DraggableLibraryItem from '../../library/DraggableLibraryItem';

type Props = {
    excalidrawAPI: ExcalidrawImperativeAPI;
    show: boolean;
    libraryGroups: LibraryGroup[];
};

const Library = React.memo((props: Props) => {
    const { t } = useTranslation('whiteboard');
    const { libraryGroups } = props;

    const getFilesForElements = useCallback((elements: ExcalidrawElement[]) => {
        const fileIds = elements
            .filter((element) => element.type === 'image' && element.fileId !== null)
            // @ts-ignore
            .map((element) => element.fileId);

        const files: BinaryFiles = {};
        fileIds.forEach((fileId) => {
            if (props.excalidrawAPI.getFiles()[fileId]) {
                files[fileId] = props.excalidrawAPI.getFiles()[fileId];
            }
        });
        return files;
    }, [props.excalidrawAPI]);

    return (
        <Box sx={{ display: props.show ? 'inherit' : 'none' }}>
            {libraryGroups.map((libraryGroup, index1) => (
                <Accordion key={index1} className="library-items" elevation={0} disableGutters>
                    <AccordionSummary
                        expandIcon={<KeyboardArrowUp/>}
                        aria-controls="panel1a-content"
                        id="panel1a-header"
                    >
                        <Typography>{t(libraryGroup.name as string)}</Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ paddingTop: 0 }}>
                        <Typography sx={{ fontWeight: 'light', fontSize: 14 }}>{t('dragToAdd')}</Typography>
                        <Grid container spacing={1} className="library-items">
                            {libraryGroup.libraryItems.map((libraryItem, index2) => (
                                <Grid key={index2} item xs={3} className="library-item">
                                    <DraggableLibraryItem libraryItem={libraryItem} files={getFilesForElements(libraryItem.elements as ExcalidrawElement[])}/>
                                </Grid>
                            ))}
                        </Grid>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
});

export default Library;
